import styled from "styled-components";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { COMPONENTS, SPACING } from "../../constants/design/spacing";

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${COMPONENTS.header.main + 112}px);
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 ${SPACING.one}px ${SPACING.five}px 0;
  box-sizing: border-box;
  scrollbar-width: thin !important;
  scrollbar-color: ${BORDER_COLORS.two} transparent !important;

  &::-webkit-scrollbar {
    display: block !important;
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${BORDER_COLORS.two};
    border-radius: 3px;
  }

  @media (max-width: 640px) {
    height: calc(100vh - ${COMPONENTS.header.mobile + 104}px);
  }
`;

export const SectionNav = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: ${SPACING.three}px;
  border-bottom: 1px solid ${BORDER_COLORS.one};
  overflow-x: auto;
`;

export const SectionNavLink = styled.button<{ isActive: boolean }>`
  ${FONT_STYLE.label.two}
  color: ${({ isActive }) => (isActive ? TEXT_COLORS.one : TEXT_COLORS.two)};
  padding: ${SPACING.two}px 0;
  border: 0;
  border-bottom: 2px solid
    ${({ isActive }) => (isActive ? COLORS.blue.one : "transparent")};
  margin-bottom: -1px;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.1s ease, border-color 0.1s ease;

  &:hover {
    color: ${TEXT_COLORS.one};
  }
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  background: ${BACKGROUND_COLORS.component};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow: visible;
`;

export const WorkspaceHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${SPACING.three}px;
  background: ${BACKGROUND_COLORS.component};
  border-radius: 8px 8px 0 0;
`;

export const ToolPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.three}px;
  padding: ${SPACING.three}px;
  min-width: 0;
`;

export const PanelToolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.three}px;

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const PanelToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;

  @media (max-width: 640px) {
    width: 100%;

    > button {
      flex: 1;
    }
  }
`;

export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.one}px;
`;

export const CardTitle = styled.div`
  ${FONT_STYLE.header.seven}
  color: ${TEXT_COLORS.one};
`;

export const CardSubtitle = styled.div`
  ${FONT_STYLE.label.two}
  color: ${TEXT_COLORS.two};
  max-width: 960px;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: ${SPACING.two}px;
  align-items: center;
`;

export const Label = styled.label`
  ${FONT_STYLE.label.one}
  color: ${TEXT_COLORS.one};
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const LabelHint = styled.span`
  ${FONT_STYLE.label.three}
  color: ${TEXT_COLORS.three};
`;

export const Input = styled.input<{ hasErrors?: boolean }>`
  ${FONT_STYLE.label.one}
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: 0 ${SPACING.two}px;
  background: ${BACKGROUND_COLORS.four};
  color: ${TEXT_COLORS.one};
  border: 1px solid
    ${({ hasErrors }) => (hasErrors ? COLORS.red.one : BORDER_COLORS.one)};
  border-radius: 4px;
  text-align: right;

  &:focus {
    outline: none;
    border-color: ${({ hasErrors }) =>
      hasErrors ? COLORS.red.one : COLORS.blue.one};
  }
`;

export const AddressInput = styled(Input)`
  text-align: left;
  min-width: 0;
`;

export const AccountWithTag = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.one}px;
  min-width: 0;

  > input {
    flex: 1 1 360px;
  }
`;

export const AccountTag = styled.span`
  ${FONT_STYLE.label.one}
  color: ${TEXT_COLORS.two};
  overflow-wrap: anywhere;
  min-width: 0;
`;

export const RulesList = styled.div`
  border-top: 1px solid ${BORDER_COLORS.one};
  border-bottom: 1px solid ${BORDER_COLORS.one};
`;

export const RuleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.two}px;
  padding: ${SPACING.three}px 0;

  & + & {
    border-top: 1px solid ${BORDER_COLORS.one};
  }
`;

export const RuleHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px;
  gap: ${SPACING.two}px;
  align-items: center;
`;

export const SourceAccount = styled.div`
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  align-items: center;
  gap: ${SPACING.two}px;
  min-width: 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: ${SPACING.one}px;
  }
`;

export const SourceLabel = styled.span`
  ${FONT_STYLE.label.three}
  color: ${TEXT_COLORS.three};
`;

export const SourceAddress = styled.span`
  ${FONT_STYLE.label.one}
  color: ${TEXT_COLORS.one};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AllocationHeader = styled.div`
  ${FONT_STYLE.label.three}
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 104px 32px;
  gap: ${SPACING.two}px;
  color: ${TEXT_COLORS.three};

  @media (max-width: 800px) {
    display: none;
  }
`;

export const AllocationRow = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 104px 32px;
  gap: ${SPACING.two}px;
  align-items: center;

  @media (max-width: 800px) {
    grid-template-columns: minmax(0, 1fr) 88px 32px;
  }
`;

export const AllocationSummary = styled.div<{ valid?: boolean }>`
  ${FONT_STYLE.label.three}
  color: ${({ valid }) => (valid ? TEXT_COLORS.two : COLORS.red.one)};
`;

export const RuleFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.two}px;

  @media (max-width: 800px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const RuleActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;

  @media (max-width: 640px) {
    width: 100%;

    > button {
      flex: 1;
    }
  }
`;

export const PercentageInputWrap = styled.div`
  position: relative;
  min-width: 0;

  input {
    padding-right: ${SPACING.four}px;
  }
`;

export const PercentageSuffix = styled.span`
  ${FONT_STYLE.label.two}
  position: absolute;
  right: ${SPACING.two}px;
  top: 50%;
  color: ${TEXT_COLORS.three};
  pointer-events: none;
  transform: translateY(-50%);
`;

export const CompactButton = styled.button<{ primary?: boolean }>`
  ${FONT_STYLE.label.two}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.one}px;
  min-height: 32px;
  padding: 0 ${SPACING.three}px;
  color: ${({ primary }) => (primary ? TEXT_COLORS.one : TEXT_COLORS.two)};
  background: ${({ primary }) =>
    primary ? COLORS.purple.two : BACKGROUND_COLORS.four};
  border: 1px solid
    ${({ primary }) => (primary ? COLORS.purple.two : BORDER_COLORS.one)};
  border-radius: 4px;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover:not(:disabled) {
    color: ${TEXT_COLORS.one};
    border-color: ${({ primary }) =>
      primary ? COLORS.purple.one : BORDER_COLORS.two};
  }

  &:disabled {
    opacity: 0.48;
  }
`;

export const IconButton = styled.button<{ negative?: boolean }>`
  ${FONT_STYLE.header.six}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: ${({ negative }) => (negative ? COLORS.red.one : TEXT_COLORS.two)};
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;

  &:hover:not(:disabled) {
    color: ${({ negative }) => (negative ? COLORS.red.one : TEXT_COLORS.one)};
    background: ${({ negative }) =>
      negative ? COLORS.red.five : BACKGROUND_COLORS.four};
    border-color: ${({ negative }) =>
      negative ? COLORS.red.two : BORDER_COLORS.one};
  }

  &:disabled {
    opacity: 0.32;
  }
`;

export const EmptyState = styled.div`
  ${FONT_STYLE.label.two}
  color: ${TEXT_COLORS.three};
  padding: ${SPACING.three}px 0;
  border-top: 1px solid ${BORDER_COLORS.one};
  border-bottom: 1px solid ${BORDER_COLORS.one};
`;

export const Textarea = styled.textarea<{ hasErrors?: boolean }>`
  ${FONT_STYLE.label.one}
  width: 100%;
  min-height: 120px;
  padding: ${SPACING.two}px;
  background: ${BACKGROUND_COLORS.four};
  color: ${TEXT_COLORS.one};
  border: 1px solid
    ${({ hasErrors }) => (hasErrors ? COLORS.red.one : BORDER_COLORS.one)};
  border-radius: 4px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ hasErrors }) =>
      hasErrors ? COLORS.red.one : COLORS.blue.one};
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.one}px;
`;

export const Chip = styled.span`
  ${FONT_STYLE.label.two}
  padding: 2px ${SPACING.one}px;
  background: ${BACKGROUND_COLORS.four};
  color: ${TEXT_COLORS.one};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 4px;
`;

export const Summary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${SPACING.two}px ${SPACING.three}px;
  background: ${BACKGROUND_COLORS.four};
  border-radius: 6px;
`;

export const SummaryLabel = styled.div`
  ${FONT_STYLE.label.two}
  color: ${TEXT_COLORS.two};
`;

export const SummaryValue = styled.div<{ valid?: boolean }>`
  ${FONT_STYLE.header.seven}
  color: ${({ valid }) => (valid ? COLORS.green.one : COLORS.red.one)};
`;

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${SPACING.two}px;
`;

export const ErrorText = styled.div`
  ${FONT_STYLE.label.three}
  color: ${COLORS.red.one};
`;
